import { REGION_OF, iso3to2 } from "@ow/shared";

/**
 * The gap between a displayed figure and its unit — "47.2 B", "63 %", "1.2 M".
 *
 * A NO-BREAK space, not a plain one: the pair is a single reading unit and must
 * never wrap with the number at the end of one line and its unit at the top of
 * the next. It is also why this is a character rather than CSS margin — most of
 * these figures are built as strings (table cells, chart ticks, badges, SVG
 * <text>, tooltips) and never reach a styled span they could be padded in.
 *
 * `withUnit` below is the only thing that should ever insert it.
 */
const UNIT_GAP = "\u00A0";

/**
 * Join a figure to its unit in house style — `withUnit("47.2", "B")` → "47.2 B".
 *
 * Whether the gap appears is decided by `affixGapEm`, the SAME predicate the
 * styled-span renderers use for their unit slot (HeroBand, the connectivity
 * columns). That shared predicate is the point: a percentage rendered as a
 * padded <span> in the 1.1 hero and as a string in a GTM table cell has to agree
 * about the space, and before this they each carried their own answer. One rule,
 * both paths — so adding a unit to the tight list can't fix half the dashboard.
 *
 * Prefixes are NOT this: "$" and "SAR" lead the number and stay tight.
 */
export const withUnit = (figure: string | number, unit: string): string =>
  `${figure}${affixGapEm(unit) > 0 ? UNIT_GAP : ""}${unit}`;

/** Signed percentage as a DISPLAYED FIGURE — "+4.2 %". Stat tiles, table cells,
 *  badges, chart labels, tooltips. For a percentage inside a running sentence use
 *  `pctProse`, which stays tight. */
export const pct = (v: number): string => withUnit(`${v >= 0 ? "+" : ""}${v.toFixed(1)}`, "%");

/**
 * A published growth rate as a DISPLAYED FIGURE, with the one case that is not a
 * number spelled out.
 *
 * `growthBasis === "new"` means the baseline block is EMPTY — the destination
 * registered nothing at all over it — so no ratio exists. (It used to mean "the
 * baseline read under F = 0.5"; the floor went on 2026-09-09 and a thin-but-real
 * base is now measured, so this is only ever a true zero.) That is a different
 * fact from "we do not
 * know", which is what "—" says, and it is the fact the reader needs: the
 * interest is younger than the window they picked. The breakout walls already
 * print NEW for exactly this (`momentumRead` in search-section-redesign.tsx), so
 * the word is the house idiom, not a new one.
 *
 * A row that registers NOTHING — no rate AND no volume — is a third fact again,
 * and it is the commonest one on the Saudi tracks: every month of the window sat
 * under Google's reporting threshold, so both blocks read zero. "New" would be a
 * false claim there (nothing registers now either) and "—" undersells it as an
 * outage, so it reads "Below threshold" (Vedant, 2026-09-09, on Sudan). It is
 * gated on `relVolume === 0` rather than on the null rate alone, because a
 * period the run's window cannot reach back far enough for — Saudi booking 5Y
 * off a 63-month run — is a coverage gap and NOT a statement about Google's
 * threshold. Those keep "—".
 *
 * Anything else with no number keeps "—".
 */
export const growthLabel = (
  v: number | null | undefined,
  basis?: "period" | "first-nonzero" | "new",
  relVolume?: number | null,
  /** The month the row's interest began, where the run could name it. Supplied
   *  by the CARD surfaces only (Vedant, 2026-09-10): the panel has the room to
   *  say when the data starts, and that is the fact a reader wants there. The
   *  ranked tables pass nothing and keep the bare word, which is what fits a
   *  narrow momentum column. */
  dataFromMonth?: string | null,
): string =>
  basis === "new"
    ? dataFromMonth
      ? // The month alone (Vedant, 2026-09-10): "/ New" is the FOOTER's pairing,
        // where the two spellings of one state are defined together. On the
        // card the month IS the statement.
        `Data available from ${dataFromMonth}`
      : relVolume === 0
        ? // A `new` verdict with NO month in the window and NO volume (Vedant,
          // 2026-09-10, Niger on Saudi 5Y): the long run saw its first signal,
          // but nothing in the current window registers, so neither a size nor
          // a start month can be stated — the same fact as the row below.
          "No data available"
        : "New"
    : v == null || !Number.isFinite(v)
      ? relVolume === 0
        ? // "Below threshold" named the CAUSE; the reader wants the consequence
          // (Vedant, 2026-09-10). Same rows, same gate, plainer words.
          "No data available"
        : "—"
      : // Grouped at the thousand (Vedant, 2026-09-09: Poland's 5Y read
        // "+13956 %", which the eye has to count digits on). `|| 0` folds the
        // -0 that `Math.round` returns for a value just under zero: `String(-0)`
        // is "0", but `Intl` formats it "-0", which would print as "+-0 %".
        withUnit(`${Math.round(v) >= 0 ? "+" : ""}${(Math.round(v) || 0).toLocaleString("en-US")}`, "%");

/**
 * Signed percentage for PROSE — "+4.2%", tight.
 *
 * The house space is for a figure presented as a figure. Inside a sentence the
 * percent sign belongs to the sentence's grammar, and "momentum above the +4.2 %
 * average of 62 tracked destinations" reads as a typo rather than a convention.
 * Use this only where the number sits in running text; everything else takes
 * `pct`.
 */
export const pctProse = (v: number): string => `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;

// Em/en dashes read as an "AI tell" in client-facing copy (LLM-generated reads,
// notes and headlines lean on them). Normalise them to a plain comma-joined
// clause (" — " → ", ") and any bare em/en dash to a hyphen, then collapse the
// double spaces that leaves. Apply to any displayed prose, static or generated.
export const tidyText = (s: string | null | undefined): string =>
  (s ?? "")
    .replace(/\s+[—–]\s+/g, ", ") // spaced dash = clause separator → comma
    .replace(/[—–]/g, "-") // any remaining em/en dash (ranges) → hyphen
    .replace(/\s{2,}/g, " ")
    .trim();

// Title-case a free-text term/destination so search phrases render as proper
// nouns ("saudi games" → "Saudi Games"). Leaves existing capitals intact.
export const titleCase = (s: string): string => s.replace(/\b\p{L}/gu, (ch) => ch.toUpperCase());

// trend is a 10-element array ending at the latest data year.
// 5Y CAGR = compound annual growth from (latestYear - 5) to latestYear,
// i.e. positions [4] → [9] inside the window. Was [3]→[8] when the
// window was [2016..2025] and slot 9 carried a preliminary year; now
// that the window ends at the latest finalized year, [4]→[9] is the
// natural 5-year endpoint.
export const cagr5y = (trend: readonly number[]): number => {
  const start = trend[4];
  const end = trend[9];
  if (!start || !end) return 0;
  return ((end / start) ** (1 / 5) - 1) * 100;
};

export const fmtSize = (s: number): string => {
  if (s >= 1) return withUnit(s.toFixed(1), "M");
  const thousands = Math.round(s * 1000);
  return s > 0 && thousands === 0 ? withUnit("<1", "K") : withUnit(thousands, "K");
};

// Adaptive compact formatter for raw counts: thousands → K, millions → M,
// billions → B. Keeps 1 decimal below 100 (e.g. 5.2K, 1.2M), drops it above
// (e.g. 117K, 250M). Sub-1000 values render as-is. Sign is preserved.
const trimUnit = (v: number): string => (v >= 100 ? Math.round(v).toString() : v.toFixed(1));
export const compactNum = (n: number): string => {
  const a = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (a >= 1e9) return withUnit(`${sign}${trimUnit(a / 1e9)}`, "B");
  if (a >= 1e6) return withUnit(`${sign}${trimUnit(a / 1e6)}`, "M");
  if (a >= 1e3) return withUnit(`${sign}${trimUnit(a / 1e3)}`, "K");
  // Sub-1000 carries no scale letter — nothing to gap.
  return `${sign}${Math.round(a)}`;
};

// Same tiers, but the figure always carries its decimals — one in thousands
// (384.6K), two in millions and billions (140.50M, 1.20B) — where compactNum
// would print 385K / 141M / 1.2B. Rolls up a tier when the rounding lands on
// 1000 so 999,960 reads 1.00M, not 1000.0K.
export const compactNum1 = (n: number): string => {
  const a = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (a < 1e3) return `${sign}${Math.round(a)}`;
  const tiers: Array<[number, string, number]> = [
    [1e3, "K", 1],
    [1e6, "M", 2],
    [1e9, "B", 2],
  ];
  let i = tiers.findIndex(([scale]) => a < scale * 1e3);
  if (i < 0) i = tiers.length - 1;
  let figure = (a / tiers[i]![0]).toFixed(tiers[i]![2]);
  if (Number(figure) >= 1000 && i < tiers.length - 1) {
    i += 1;
    figure = (a / tiers[i]![0]).toFixed(tiers[i]![2]);
  }
  return withUnit(`${sign}${figure}`, tiers[i]![1]);
};

// Many sections carry values pre-scaled to a fixed unit (arrivals/departures in
// MILLIONS, spend in BILLIONS). Appending a hard "M"/"B" suffix then misreads
// small markets — 0.3B shows as "$0.3B" instead of "$300M", 0.4M as "0.4M"
// instead of "400K". These rescale to a raw count and hand off to compactNum so
// the unit adapts (K → M → B) to each value's actual magnitude.
/** Format a value expressed in MILLIONS (e.g. 0.4 → "400K", 36.5 → "36.5M", 1200 → "1.2B"). */
export const fmtFromMillions = (v: number): string => compactNum(v * 1e6);
/** Format a value expressed in BILLIONS (e.g. 0.3 → "300M", 120.4 → "120B"). */
export const fmtFromBillions = (v: number): string => compactNum(v * 1e9);
/** Money variant of fmtFromMillions — prefixes "$" (e.g. 0.4 → "$400K"). */
export const usdFromMillions = (v: number): string => `$${compactNum(v * 1e6)}`;
/** Money variant of fmtFromBillions — prefixes "$" (e.g. 0.3 → "$300M"). */
export const usdFromBillions = (v: number): string => `$${compactNum(v * 1e9)}`;

// alpha-3 → alpha-2 mapping now lives in @ow/shared (single source of truth);
// imported above and re-exported here so existing dashboard imports keep working.
export { iso3to2 };

export const flagEmoji = (iso3: string): string => {
  const iso2 = iso3to2[iso3];
  if (!iso2) return "";
  return iso2.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
};

// Region palette references the themeable series/region tokens (tokens.css)
// so regions stay legible on dark — applied as inline styles / SVG fills,
// both of which resolve CSS var().
export const regionColors: Record<string, string> = {
  Americas: "var(--color-series-arrivals)",
  Europe: "var(--color-series-purple)",
  "Middle East": "var(--color-region-mideast)",
  "Asia Pacific": "var(--color-region-asiapac)",
  Africa: "var(--color-series-emerging)",
};

export const countryName: Record<string, string> = {
  USA: "United States",
  MEX: "Mexico",
  CAN: "Canada",
  BRA: "Brazil",
  GBR: "United Kingdom",
  FRA: "France",
  DEU: "Germany",
  ESP: "Spain",
  ITA: "Italy",
  GRC: "Greece",
  TUR: "Türkiye",
  AUT: "Austria",
  NLD: "Netherlands",
  POL: "Poland",
  PRT: "Portugal",
  CHE: "Switzerland",
  BEL: "Belgium",
  RUS: "Russia",
  SAU: "Saudi Arabia",
  ARE: "United Arab Emirates",
  QAT: "Qatar",
  EGY: "Egypt",
  MAR: "Morocco",
  JOR: "Jordan",
  CHN: "China",
  JPN: "Japan",
  KOR: "South Korea",
  HKG: "Hong Kong",
  THA: "Thailand",
  IND: "India",
  IDN: "Indonesia",
  MYS: "Malaysia",
  VNM: "Vietnam",
  PHL: "Philippines",
  SGP: "Singapore",
  PAK: "Pakistan",
  BGD: "Bangladesh",
  AUS: "Australia",
  ZAF: "South Africa",
  NGA: "Nigeria",
  KEN: "Kenya",
  // Region IDs used by /corridors when the source is a UN region rather
  // than a country (real data from UN's destination × source-region file).
  EUR: "Europe",
  AMR: "Americas",
  APC: "Asia Pacific",
  MEA: "Middle East",
  AFR: "Africa",
};

export const REGION_IDS = new Set(["EUR", "AMR", "APC", "MEA", "AFR"]);

// ICU English display name for every ISO3 the shared map knows — so any market
// (all ~180 in the corridor set, not just the hand-curated ~40 in `countryName`)
// resolves to a real country name instead of falling back to its bare code.
const ICU_COUNTRY_NAME: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  try {
    const names = new Intl.DisplayNames(["en"], { type: "region" });
    for (const [iso3, iso2] of Object.entries(iso3to2)) {
      const name = names.of(iso2);
      if (name && name !== iso2) out[iso3] = name;
    }
  } catch {
    /* no ICU region data — curated `countryName` still covers the common markets */
  }
  return out;
})();

/** ISO3 (or region id) → display name. Curated `countryName` wins (keeps region
 *  labels + preferred forms like "South Korea"/"Türkiye"), then ICU, then the
 *  code itself as a last resort. Use this anywhere a code list is shown to users. */
export const countryLabel = (code: string): string => countryName[code] ?? ICU_COUNTRY_NAME[code] ?? code;

/**
 * ISO3 → the dashboard's 5-region label.
 *
 * Re-exported from `@ow/shared`, which is where this map now lives so the
 * server can group by the same regions it renders. The local copy this
 * replaces had drifted to 41 countries while the shared one covers 245 — and
 * every caller defaulted a miss to a LITERAL "Asia Pacific", so 142 of the
 * 182 Saudi source markets were published under the wrong region: Bahrain,
 * Argentina and Angola all read "Asia Pacific" on the map panel.
 */
export const regionOf: Record<string, string> = REGION_OF;

export const nameAliases: Record<string, string> = {
  USA: "United States",
  "United States of America": "United States",
  UK: "United Kingdom",
  Türkiye: "Turkey",
  Korea: "South Korea",
  "Czech Rep.": "Czechia",
  UAE: "United Arab Emirates",
  "Russian Federation": "Russia",
};

/**
 * Does this value-affix read as a WORD, and so need a real space before it?
 *
 * The stat renderers (HeroBand in `hero-row.tsx`, the connectivity columns) sit
 * a number and its affix in one flex row at `gap: 2px`. That is right for a
 * scale or symbol suffix — "5.2M", "1.4T", "62%", "/100" are conventionally
 * tight — but the SAME slot also carries plain nouns: seats, routes, airports,
 * nights, destinations. At 2px those glue to the number ("1,204,338seats"), and
 * because HeroBand also backs every signal strip via `SignalBar`, one narrow gap
 * was doing it across Search, Booking, Sentiment and Connectivity at once.
 *
 * An affix is a word when it STARTS with three or more letters. Anchoring only
 * at the start matters: several affixes are multi-word ("source markets",
 * "unique experiences", "products - all variants"), and a whole-string
 * `[A-Za-z]{3,}` test rejected exactly the ones that most need the space.
 *
 * That keeps the tight suffixes tight: single-letter scales (M/B/T), "%", "/100"
 * and "pp" (percentage points, conventionally set close — "+3pp") are all under
 * three leading letters and stay exactly as they were. Nothing here removes
 * existing spacing; it only adds it where a noun ran into a digit.
 */
export const isWordAffix = (affix: string): boolean => /^[A-Za-z]{3,}/.test(affix.trim());

/**
 * Magnitude-scale suffix: a lone K / M / B / T (thousand … trillion). The usual
 * typographic convention sets these tight ("5.2M", "1.4T"), but the client
 * prefers a real space before them too — "5.2 M", "1.4 T", "1.53 B" — so they are
 * gapped alongside word affixes. Deliberately narrow: it matches ONLY a single
 * scale letter, so a multiplier ("4.6x"), "/100" and symbols (°) stay tight.
 */
export const isScaleAffix = (affix: string): boolean => /^[KMBT]$/.test(affix.trim());

/**
 * Percentage affix — "%" and its sibling "pp" (percentage points).
 *
 * These used to be set tight ("84%", "+3pp") on the usual English convention,
 * while the magnitude scales beside them were gapped ("47.2 B"). In the 1.1 hero
 * band that put both treatments in ONE row of four identically-styled affixes —
 * "31.7 M" and "$47.2 B" spaced, "+84%" and "63%" glued — which reads as a bug
 * rather than a convention. The client's rule is that the space is the house
 * style for a unit affix, so percent joins the gapped group; "pp" comes with it,
 * because a tight "+3pp" sitting next to a spaced "63 %" just moves the same
 * inconsistency one tile over.
 *
 * Still tight, deliberately: a multiplier ("4.6x") and a fraction ("78/100")
 * bind to the number grammatically rather than annotating it, and the degree
 * symbol ("18°") is set closed in every convention.
 */
export const isPercentAffix = (affix: string): boolean => /^(%|pp)$/.test(affix.trim());

/** Extra left margin for a value affix, in em of the affix's own font size, so it
 *  scales with whichever stat renderer is using it. Sits on top of the existing
 *  2px flex gap. Applied to word affixes, magnitude-scale letters and percent
 *  (client preference); zero for multipliers, "/100" and symbols, which must not move. */
export const affixGapEm = (affix: string): number =>
  isWordAffix(affix) || isScaleAffix(affix) || isPercentAffix(affix) ? 0.3 : 0;
